import type dynamicIconImports from 'lucide-react/dynamicIconImports';
import { URLS } from '@/lib/consts';
import { type ServiceURLs, ServiceType } from '../../types';

/*
 * Page Data
 */
export const FEATURES_AND_BENEFITS_SECTION_ID = 'features-and-benefits';
export const FEATURES_AND_BENEFITS_SUB_TITLE = 'FEATURES & BENEFITS';

export const CTA_BUTTON_TEXT = 'View Solutions';

/*
 * Type Definitions
 */
export type IconName = keyof typeof dynamicIconImports;

interface FeatureItem {
  title: string;
  content: string;
  icon: IconName;
}

interface FeaturesAndBenefitsSectionData {
  title: string;
  subtitle: string;
  description: string;
  features: FeatureItem[];
}

/*
 * Generates Features & Benefits Section data based on service type.
 */
function generateFeaturesAndBenefitsData(
  serviceType: ServiceType,
): FeaturesAndBenefitsSectionData {
  switch (serviceType) {
    case ServiceType.BUILD: {
      return {
        title: 'Building Success',
        subtitle: 'Design & Build Successfully',
        description: `Embark on your journey to creating the home of your dreams with a trusted partner by your side. At Cornerstone Homes, we understand the importance of success in every aspect of your project. That\u2019s why we offer an in-house design team & 3D walkthroughs of your home before you build.`,
        features: [
          {
            title: 'Project Management',
            content: `Craig has been building homes across Claremore, Rogers County, and the Tulsa metro area for over 20 years. Cornerstone excels at managing timelines and resources to ensure your project\u2019s success. From the initial site evaluation through the final walkthrough, we coordinate every trade, inspection, and material delivery so you can focus on the exciting parts of building your new home\u2014not the logistics.`,
            icon: 'clipboard-check',
          },
          {
            title: 'Trade Connections & Technical Knowledge',
            content: `Cornerstone has built strong, long-standing relationships with trade partners, suppliers, and highly skilled subcontractors throughout Rogers County and northeastern Oklahoma. Coupled with our deep understanding of building materials, soil conditions, and regional building codes, we are equipped to handle any challenge that arises during construction\u2014from foundation engineering on Rogers County clay to energy-efficient framing techniques suited for Oklahoma weather.`,
            icon: 'handshake',
          },
          {
            title: 'Accurate Budgeting',
            content: `We believe in transparency and accuracy when it comes to your investment. Cornerstone develops a detailed, line-item budget before construction begins and maintains it in real time throughout the build. When material selections come up, we present options at multiple price points so you stay in control. Our clients in the Claremore and Tulsa metro area consistently tell us that our budget accuracy\u2014often within one percent of the original estimate\u2014is one of the top reasons they chose us.`,
            icon: 'circle-dollar-sign',
          },
          {
            title: 'Permits',
            content: `Navigating permits and inspections can be daunting, but we handle it all. Our team understands the permitting requirements for Rogers County, Mayes County, and municipal jurisdictions throughout the Tulsa metro area. We coordinate every application, inspection, and approval so you never have to visit a county office or chase down paperwork\u2014saving you time and hassle at every stage of the build.`,
            icon: 'newspaper',
          },
          {
            title: 'Covered By Warranty',
            content: `Rest easy knowing that your investment is protected by our comprehensive warranty covering structural elements, major systems, and workmanship. We stand behind every home we build in Claremore and the surrounding communities with confidence, and our post-construction support team is available for ongoing assistance with future projects such as outdoor living spaces, pools, fencing, and landscaping.`,
            icon: 'file-badge',
          },
        ],
      };
    }

    case ServiceType.RENOVATE: {
      return {
        title: 'Renovation Success',
        subtitle: 'Achieving Renovation Success',
        description: `Transform your existing space into your dream home with the help of Cornerstone Homes in Claremore, Rogers County, and the Tulsa metro area. With our integrated design-build approach to renovation, we guide you through every step—from initial design to final walkthrough—to ensure your project is completed on time, on budget, and beyond expectations.`,
        features: [
          {
            title: 'Project Management',
            content: `Craig has been managing residential renovation projects across Claremore, Rogers County, and the Tulsa metro area for over 20 years. Cornerstone excels at coordinating timelines, trades, and resources to keep your renovation on track. From the initial demolition through the final punch list, we manage every subcontractor, inspection, and material delivery so you can focus on the exciting transformation of your home—not the logistics of a construction site.`,
            icon: 'clipboard-check',
          },
          {
            title: 'Trade Connections & Technical Knowledge',
            content: `Cornerstone has built strong, long-standing relationships with trade partners, suppliers, and highly skilled subcontractors throughout Rogers County and northeastern Oklahoma. Our deep understanding of existing home systems—electrical, plumbing, HVAC, and structural—means we can diagnose issues quickly and integrate new work seamlessly with your home's existing infrastructure. This expertise is critical during renovations where hidden conditions behind walls and under floors require experienced problem-solving.`,
            icon: 'handshake',
          },
          {
            title: 'Accurate Budgeting',
            content: `We believe in transparency and accuracy when it comes to your renovation investment. Cornerstone develops a detailed, line-item budget before construction begins and maintains it in real time throughout the project. When material selections come up, we present options at multiple price points so you stay in control. Our clients in the Claremore and Tulsa metro area consistently tell us that our budget accuracy is one of the top reasons they chose us for their renovation.`,
            icon: 'circle-dollar-sign',
          },
          {
            title: 'Permits',
            content: `Navigating permits and inspections for renovation projects can be complex, especially when structural modifications are involved—but we handle it all. Our team understands the permitting requirements for Rogers County, Mayes County, and municipal jurisdictions throughout the Tulsa metro area. We coordinate every application, inspection, and approval so you never have to visit a county office or chase down paperwork, saving you time and hassle at every stage of your renovation.`,
            icon: 'newspaper',
          },
          {
            title: 'Covered By Warranty',
            content: `Rest easy knowing that your renovation investment is protected by our comprehensive warranty covering structural modifications, major systems, and workmanship. We stand behind every renovation we complete in Claremore and the surrounding communities. Our post-construction support team is available for ongoing assistance, ensuring that your renovated space continues to perform and look its best for years to come across Rogers County and the Tulsa metro.`,
            icon: 'file-badge',
          },
        ],
      };
    }

    case ServiceType.ROOT: {
      return {
        title: '',
        subtitle: '',
        description: '',
        features: [],
      };
    }

    default: {
      throw new Error(
        'ServiceType provided dose not exist for Features & Benefits Section',
      );
    }
  }
}

const ROOT_FEATURES = generateFeaturesAndBenefitsData(ServiceType.ROOT);
const BUILD_FEATURES = generateFeaturesAndBenefitsData(ServiceType.BUILD);
const RENOVATE_FEATURES = generateFeaturesAndBenefitsData(ServiceType.RENOVATE);

const FEATURES_AND_BENEFITS_DATA: Record<
  ServiceURLs,
  FeaturesAndBenefitsSectionData
> = {
  [URLS.SERVICES.ROOT]: ROOT_FEATURES,
  [URLS.SERVICES.DESIGN_AND_BUILD]: BUILD_FEATURES,
  [URLS.SERVICES.DESIGN_AND_RENOVATE]: RENOVATE_FEATURES,
};

export default FEATURES_AND_BENEFITS_DATA;
